import { useState } from "react";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import DemoModeDialog from "shared/ui/DemoModeDialog";
import ProgramRow from "features/programs/components/ProgramRow";
import NewProgram from "features/programs/components/NewProgram";
import ConfirmDeleteProgramDialog from "features/programs/components/ConfirmDeleteProgramDialog";
import ViewportTooSmall from "features/planner/components/planner/ViewportTooSmall";
import useViewportInfo from "hooks/useViewportInfo";

import { usePrograms } from "features/programs/state/ProgramsProvider";
import { useNavigate } from "react-router-dom";
import { isDemoMode } from "shared/config/appConfig";

function ProgramsPage() {
  const { width, height } = useViewportInfo("Programs viewport");
  const { programs, loading, selectedProgramId, selectProgram, deleteProgram, duplicateProgram } =
    usePrograms();

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuProgramId, setMenuProgramId] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  const navigate = useNavigate();

  if (width < 1200 || height < 630) {
    return <ViewportTooSmall />;
  }

  const openMenu = (event, programId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuProgramId(programId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuProgramId(null);
  };

  const handleLoad = () => {
    const programId = menuProgramId;
    closeMenu();
    navigate(`/planner/${programId}`);
  };

  const handleDoubleClickLoad = (programId) => {
    navigate(`/planner/${programId}`);
  };

  const handleDelete = async () => {
    await deleteProgram(programToDelete.id);

    setDeleteDialogOpen(false);
    setProgramToDelete(null);
  };

  const handleDuplicate = async () => {
    const programId = menuProgramId;
    closeMenu();

    if (isDemoMode) {
      setIsDemoDialogOpen(true);
      return;
    }

    await duplicateProgram(programId);
  };

  const handleDeleteRequest = () => {
    if (isDemoMode) {
      closeMenu();
      setIsDemoDialogOpen(true);
      return;
    }

    const selectedProgram = programs.find((program) => program.id === menuProgramId);
    setProgramToDelete(selectedProgram);
    setDeleteDialogOpen(true);
    closeMenu();
  };

  return (
    <>
      <MDBox
        sx={{
          width: "100%",
          borderRadius: "12px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "background.default",
        }}
      >
        <Card
          sx={{
            height: "100%",
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <MDBox
            p={3}
            sx={{
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            <MDTypography variant="h4" fontWeight="bold">
              Mis rutinas
            </MDTypography>

            <Divider sx={{ my: 2 }} />

            {/* Header */}
            <MDBox
              display="grid"
              gridTemplateColumns="3fr 2fr 2fr 2fr 48px"
              alignItems="center"
              px={2}
              py={1}
              borderBottom="1px solid"
              borderColor="grey.300"
            >
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                Programa
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                Cliente
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                Inicio
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                Fin
              </MDTypography>

              <MDBox display="flex" justifyContent="flex-end">
                <NewProgram
                  onClick={() => {
                    navigate("/planner/new");
                  }}
                />
              </MDBox>
            </MDBox>

            {/* Filas */}
            {loading ? (
              <MDBox py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                <CircularProgress size={22} />
                <MDTypography variant="body2" color="text">
                  Cargando programas...
                </MDTypography>
              </MDBox>
            ) : (
              programs.map((program) => {
                const isSelected = program.id === selectedProgramId;

                return (
                  <ProgramRow
                    key={program.id}
                    program={program}
                    isSelected={isSelected}
                    onSelect={() => selectProgram(program.id)}
                    onOpenMenu={openMenu}
                    onDoubleClick={() => handleDoubleClickLoad(program.id)}
                  />
                );
              })
            )}
          </MDBox>
        </Card>

        {/* Menú acciones */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem onClick={handleLoad}>
            <PlayArrowIcon fontSize="small" style={{ marginRight: 8 }} />
            Cargar
          </MenuItem>
          <MenuItem onClick={handleDuplicate}>
            <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} />
            Duplicar
          </MenuItem>
          <MenuItem onClick={handleDeleteRequest} sx={{ color: "error.main" }}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} />
            Eliminar
          </MenuItem>
        </Menu>
      </MDBox>

      {/* Modal confirmación */}
      <ConfirmDeleteProgramDialog
        open={deleteDialogOpen}
        program={programToDelete}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setProgramToDelete(null);
        }}
      />
      <DemoModeDialog open={isDemoDialogOpen} onClose={() => setIsDemoDialogOpen(false)} />
    </>
  );
}

export default ProgramsPage;
